import { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ToastProvider } from "./context/ToastContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CitySelectionProvider } from "./context/CitySelectionContext";
import { I18nProvider } from "./i18n";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import RouteFallback from "./components/RouteFallback";
import AgeVerification from "./components/AgeVerification";
import MetrikaTracker from "./components/MetrikaTracker";

// Route-level code splitting. Every page is its own chunk so an anonymous
// visitor hitting /places never downloads the dashboard, moderation, or
// settings bundles. Suspense fallback renders the skeleton layout while
// the chunk streams in.
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const TapFormPage = lazy(() => import("./pages/TapFormPage"));
const PlacesCatalogPage = lazy(() => import("./pages/PlacesCatalogPage"));
const PublicBeersCatalogPage = lazy(() => import("./pages/PublicBeersCatalogPage"));
const BeerDetailPage = lazy(() => import("./pages/BeerDetailPage"));
const BreweriesCatalogPage = lazy(() => import("./pages/BreweriesCatalogPage"));
const BreweryPage = lazy(() => import("./pages/BreweryPage"));
const PlacePage = lazy(() => import("./pages/PlacePage"));
const ScreenModePage = lazy(() => import("./pages/ScreenModePage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const VerificationPendingPage = lazy(() => import("./pages/VerificationPendingPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ConfirmEmailChangePage = lazy(() => import("./pages/ConfirmEmailChangePage"));
const ConfirmPasswordChangePage = lazy(() => import("./pages/ConfirmPasswordChangePage"));
const ModerationPage = lazy(() => import("./pages/ModerationPage"));
const ModerationCatalogPage = lazy(() => import("./pages/ModerationCatalogPage"));
const ModerationBreweriesPage = lazy(() => import("./pages/ModerationBreweriesPage"));
const ModerationPlacesPage = lazy(() => import("./pages/ModerationPlacesPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const OfferPage = lazy(() => import("./pages/OfferPage"));
const RefundPage = lazy(() => import("./pages/RefundPage"));
const ForOwnersPage = lazy(() => import("./pages/ForOwnersPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <I18nProvider>
          <AgeVerification>
            <AuthProvider>
              <ToastProvider>
                <CitySelectionProvider>
                  <MetrikaTracker />
                  <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      <Route path="/" element={<PlacesCatalogPage limit={12} />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/verify-email" element={<VerifyEmailPage />} />
                      <Route path="/verify-email-sent" element={<VerificationPendingPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                      <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
                      <Route
                        path="/confirm-password-change"
                        element={<ConfirmPasswordChangePage />}
                      />
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <DashboardPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/settings"
                        element={
                          <ProtectedRoute>
                            <SettingsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/tap/new"
                        element={
                          <ProtectedRoute>
                            <TapFormPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/tap/:id/edit"
                        element={
                          <ProtectedRoute>
                            <TapFormPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/moderation"
                        element={
                          <ProtectedRoute>
                            <ModerationPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/moderation/catalog"
                        element={
                          <ProtectedRoute>
                            <ModerationCatalogPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/moderation/breweries"
                        element={
                          <ProtectedRoute>
                            <ModerationBreweriesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/moderation/places"
                        element={
                          <ProtectedRoute>
                            <ModerationPlacesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/cookies" element={<CookiesPage />} />
                      <Route path="/offer" element={<OfferPage />} />
                      <Route path="/refund" element={<RefundPage />} />
                      <Route path="/for-owners" element={<ForOwnersPage />} />
                      <Route path="/faq" element={<FaqPage />} />
                      <Route path="/places" element={<PlacesCatalogPage hideHero />} />
                      <Route path="/beers" element={<PublicBeersCatalogPage />} />
                      <Route path="/beers/:id" element={<BeerDetailPage />} />
                      <Route path="/breweries" element={<BreweriesCatalogPage />} />
                      <Route path="/brewery/:slug" element={<BreweryPage />} />
                      <Route path="/place/:slug" element={<PlacePage />} />
                      {/* Menu-only view: stripped-down /place/:slug for QR codes
                        on tables — no photos, no "about", no contacts. The QR
                        generated in owner settings points here. */}
                      <Route path="/place/:slug/menu" element={<PlacePage menuOnly />} />
                      {/* Screen mode handles auth itself: either via the owner's
                        session cookie (with ownership check) or via a ?t= display
                        token. A ProtectedRoute wrapper would break the TV /
                        tablet use case, where the device has no cookie. */}
                      <Route path="/place/:slug/screen" element={<ScreenModePage />} />
                      {/* Catch-all — anything unrouted lands on a useful
                          404 page with noindex meta, instead of the blank
                          screen the bare <Routes> would render. */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </CitySelectionProvider>
              </ToastProvider>
            </AuthProvider>
          </AgeVerification>
        </I18nProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
